import { useEffect, useRef, type ReactNode, type Ref } from "react";

import {
  useTrack,
  type SectionId,
  type SectionVariant,
} from "@/analytics/events";
import { SectionProvider } from "@/analytics/SectionContext";

/** Long enough that flinging past a section does not count as seeing it. */
const DWELL_MS = 400;
/** A tall section only ever reaches a small ratio, so sample finely. */
const THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20);

type SectionProps = {
  /** Analytics section id; also the element id unless `elementId` overrides. */
  id: SectionId;
  /** 0-based index of this section within the page. */
  position: number;
  /** Only for sections that render more than one UI. */
  variant?: SectionVariant | undefined;
  /** Keeps an existing page anchor when it differs from the analytics id. */
  elementId?: string;
  /** Id of the heading that names this section, for assistive tech. */
  ariaLabelledBy?: string;
  /** Names the section directly when no single heading can — e.g. sibling
      sections sharing one visible heading, which must not share one name. */
  ariaLabel?: string;
  ref?: Ref<HTMLElement>;
  className?: string;
  children: ReactNode;
};

/**
 * A page section that reports itself.
 *
 * Fires `section_viewed` once per pageview and provides `section` to every
 * event tracked beneath it.
 */
const Section = ({ id, children, ...rest }: SectionProps) => (
  <SectionProvider value={id}>
    {/* Inside the provider, so it tracks with its own section. */}
    <SectionElement sectionId={id} {...rest}>
      {children}
    </SectionElement>
  </SectionProvider>
);

const SectionElement = ({
  sectionId,
  position,
  variant,
  elementId,
  ariaLabelledBy,
  ariaLabel,
  ref,
  className,
  children,
}: Omit<SectionProps, "id"> & { sectionId: SectionId }) => {
  const track = useTrack();
  const elementRef = useRef<HTMLElement>(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (
      element === null ||
      viewedRef.current ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    let dwell: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry === undefined) {
          return;
        }
        // Half the section, or half a viewport of it — whichever is smaller.
        // The how-it-works scrub track is several viewports tall, so a bare
        // `intersectionRatio >= 0.5` would mean it never reports a view.
        const needed = Math.min(
          entry.boundingClientRect.height / 2,
          window.innerHeight / 2,
        );
        const seen =
          entry.isIntersecting && entry.intersectionRect.height >= needed;

        if (!seen) {
          clearTimeout(dwell);
          dwell = undefined;
          return;
        }
        if (dwell !== undefined) {
          return;
        }
        dwell = setTimeout(() => {
          viewedRef.current = true;
          observer.disconnect();
          track(
            "section_viewed",
            variant === undefined ? { position } : { position, variant },
          );
        }, DWELL_MS);
      },
      { threshold: THRESHOLDS },
    );
    observer.observe(element);

    return () => {
      clearTimeout(dwell);
      observer.disconnect();
    };
  }, [track, position, variant]);

  const setRefs = (node: HTMLElement | null) => {
    elementRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref !== undefined && ref !== null) {
      ref.current = node;
    }
  };

  return (
    <section
      id={elementId ?? sectionId}
      ref={setRefs}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </section>
  );
};

export default Section;
